import React from 'react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { Box } from '@mui/material';
import { createActionHelp } from '../../../../shared/ui/common/helpModel';
import { quickFillActionTooltips } from '../../../../shared/config';
import {
  ResponsiveToolbarButton,
  ToolbarButtonGroup,
} from '../../../../shared/ui/workflow';

const LocalDictionaryHelp = createActionHelp(
  'Local dictionary',
  'Store completed Auto-fill values from Revit families in a local dictionary on this workstation. To add values to the dictionary, select at least one row and click Add Selected.',
  {
    downstream: 'Built-in Auto-fill rules continue to take priority. You can export the local JSON dictionary to share it with coworkers.',
    note: 'The local dictionary is not uploaded to the server or saved in a profile.',
  }
);

export interface QuickFillAutoFillControlsProps {
  hasData: boolean;
  hasSelection: boolean;
  keyLibraryRowsReady: boolean;
  showActionText: boolean;
  buttonSize: 'small' | 'medium';
  groupDensity: 'compact' | 'standard';
  controlsRef?: React.RefObject<HTMLDivElement | null>;
  onAutoFillAll: () => void;
  onAutoFillSelected: () => void;
  onAddToKeyLibrary: () => void;
}

export const QuickFillAutoFillControls: React.FC<QuickFillAutoFillControlsProps> = ({
  hasData,
  hasSelection,
  keyLibraryRowsReady,
  showActionText,
  buttonSize,
  groupDensity,
  controlsRef,
  onAutoFillAll,
  onAutoFillSelected,
  onAddToKeyLibrary,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flex: '0 0 auto' }}>
    <ToolbarButtonGroup
      ref={controlsRef}
      aria-label="Auto-fill controls"
      data-testid="quickfill-tour-target-auto-fill-controls"
      density={groupDensity}
      sx={{ flexWrap: 'nowrap', flex: '0 0 auto', width: 'auto' }}
    >
      <ResponsiveToolbarButton
        icon={<AutoFixHighIcon sx={{ fontSize: showActionText ? '0.95rem !important' : '1rem' }} />}
        label="Auto-fill"
        aria-label="Auto-fill all rows"
        showText={showActionText}
        variant="contained"
        color="secondary"
        disabled={!hasData}
        onClick={onAutoFillAll}
        help={quickFillActionTooltips.autoFillAll}
        disabledHelp={quickFillActionTooltips.autoFillAllDisabled}
        data-testid="quickfill-auto-fill-all"
        size={buttonSize}
      />
      <ResponsiveToolbarButton
        icon={<AutoFixHighIcon sx={{ fontSize: showActionText ? '0.95rem !important' : '1rem' }} />}
        label="Selected"
        aria-label="Auto-fill selected rows"
        showText={showActionText}
        variant="outlined"
        color="secondary"
        disabled={!hasSelection}
        onClick={onAutoFillSelected}
        help={quickFillActionTooltips.autoFillSelected}
        disabledHelp={quickFillActionTooltips.autoFillSelectedDisabled}
        data-testid="quickfill-auto-fill-selected"
        size={buttonSize}
      />
      <ResponsiveToolbarButton
        icon={<MenuBookOutlinedIcon sx={{ fontSize: '1rem' }} />}
        label={hasSelection ? 'Add selected' : 'Dictionary'}
        aria-label={hasSelection ? 'Add selected rows to dictionary' : 'Open dictionary'}
        showText={showActionText}
        variant="outlined"
        color="secondary"
        disabled={hasSelection && !keyLibraryRowsReady}
        onClick={onAddToKeyLibrary}
        help={LocalDictionaryHelp}
        size={buttonSize}
      />
    </ToolbarButtonGroup>
  </Box>
);
